import { AvailabilityListHeading } from '@shared/availability/AvailabilityListHeading';
import { AvailableTimes } from '@shared/availability/AvailableTimes';
import { groupTimesByServiceWindow } from '@shared/availability/availabilityUtil';
import cx from 'classnames';
import { useMemo, type ReactElement } from 'react';
import typography from '~styles/typography.scss';
import { AdminAvailabilityData } from './apiHelpers';
import styles from './ConciergeAdminAvailability.scss';
import { ConciergeAdminAvailabilityItem } from './ConciergeAdminAvailabilityItem';
import { useConciergeAvailabilityContext } from './ConciergeAvailabilityContext';
import { NoTables } from './NoTables';

interface ConciergeAdminAvailabilityProps {
  availabilityItemAction: ReactElement;
}

export const ConciergeAdminAvailability = ({
  availabilityItemAction,
}: ConciergeAdminAvailabilityProps) => {
  const {
    filteredAvailabilities,
    filteredAvailabilitiesGroupedByTime,
    selectedTime,
    setSelectedTime,
    clearSelectedTime,
    selectedFloorPlanListingIds,
  } = useConciergeAvailabilityContext();

  const tablePublicName = useMemo(() => {
    if (selectedFloorPlanListingIds.length !== 1 || !selectedTime) {
      return undefined;
    }
    const listingId = selectedFloorPlanListingIds[0];
    const atTime = filteredAvailabilitiesGroupedByTime[selectedTime] ?? [];
    const fromTime = atTime.find((a) => a.listing.id === listingId);
    const raw =
      fromTime?.listing.publicName ??
      filteredAvailabilities.find((a) => a.listing.id === listingId)?.listing
        .publicName;
    const trimmed = raw?.trim();
    return trimmed || undefined;
  }, [
    selectedFloorPlanListingIds,
    selectedTime,
    filteredAvailabilitiesGroupedByTime,
    filteredAvailabilities,
  ]);

  if (!filteredAvailabilities.length) return <NoTables />;

  const availableTimesGroupedByServiceWindow = groupTimesByServiceWindow(
    filteredAvailabilitiesGroupedByTime,
  );

  const serviceWindows = availableTimesGroupedByServiceWindow.map(
    ([serviceWindow, _times]) => serviceWindow,
  );

  // A Best available availability is guaranteed to be present due to upstream
  // checks. It will never be undefined.
  const bestAvailableAvailability = filteredAvailabilities.find(
    (availability) => availability.isBestAvailable,
  ) as AdminAvailabilityData;

  // A featured upgrade availability is _not_ guaranteed to present. In the case
  // where the featured upgrade availability is the same as the best available
  // availability we shouldn't show the same availability in both places since
  // that's likely more confusing than helpful
  const featuredUpgradeAvailability = filteredAvailabilities.find(
    (availability) =>
      availability.isFeaturedUpgrade && !availability.isBestAvailable,
  ) as AdminAvailabilityData | undefined;

  const userHasNotSelectedAListingOnFloorPlan =
    !selectedFloorPlanListingIds.length;

  return (
    <div data-testid="availability-container" className={styles.container}>
      {selectedTime ? (
        <>
          {tablePublicName ? (
            <p
              className={cx(typography.h7_20, styles.tablePublicNameTop)}
              data-testid="availability-selected-table-name"
            >
              {tablePublicName}
            </p>
          ) : null}
          <AvailabilityListHeading
            clearSelectedTime={clearSelectedTime}
            selectedTime={selectedTime}
          />

          {userHasNotSelectedAListingOnFloorPlan && (
            <>
              <ul
                className={styles.list}
                aria-label="Best Available Availability"
              >
                <ConciergeAdminAvailabilityItem
                  availability={bestAvailableAvailability}
                  availabilityItemAction={availabilityItemAction}
                  key={
                    bestAvailableAvailability.listing.id +
                    bestAvailableAvailability.time +
                    bestAvailableAvailability.isBestAvailable
                  }
                  isFlaggedAvailabilityItem
                />
              </ul>

              {featuredUpgradeAvailability && (
                <div className={styles.availabilityContainer}>
                  <span className={typography.h7_20}>Featured Upgrade</span>
                  <ul
                    className={styles.list}
                    aria-label="Featured Upgrade Availability"
                  >
                    <ConciergeAdminAvailabilityItem
                      availability={featuredUpgradeAvailability}
                      availabilityItemAction={availabilityItemAction}
                      key={
                        featuredUpgradeAvailability.listing.id +
                        featuredUpgradeAvailability.time +
                        featuredUpgradeAvailability.isFeaturedUpgrade
                      }
                      isFlaggedAvailabilityItem
                    />
                  </ul>
                </div>
              )}
            </>
          )}

          <div className={styles.availabilityContainer}>
            {userHasNotSelectedAListingOnFloorPlan && (
              <span className={typography.h7_20}>All Tables</span>
            )}
            <ul className={styles.list} aria-label="Restaurant Availabilities">
              {filteredAvailabilities.map((a) => (
                <ConciergeAdminAvailabilityItem
                  availability={a}
                  availabilityItemAction={availabilityItemAction}
                  key={a.listing.id + a.time}
                />
              ))}
            </ul>
          </div>
        </>
      ) : (
        <AvailableTimes
          // @ts-expect-error-next-line
          filteredAvailabilitiesGroupedByTime={
            filteredAvailabilitiesGroupedByTime
          }
          serviceWindows={serviceWindows}
          availableTimesGroupedByServiceWindow={
            availableTimesGroupedByServiceWindow
          }
          onTimeSelect={setSelectedTime}
        />
      )}
    </div>
  );
};
